import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import PasswordInput from "@/components/PasswordInput";

export default function Settings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => setS(data));
  }, []);

  if (!s) return <div className="text-[#6b655c]">Yükleniyor…</div>;

  const set = (k, v) => setS((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", s);
      toast.success("Ayarlar kaydedildi");
    } catch {
      toast.error("Hata");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-settings-page">
      <div>
        <span className="label-tag">Sistem</span>
        <h1 className="font-serif text-4xl mt-2">Ayarlar</h1>
      </div>

      <Section title="Site">
        <Field label="Site Başlığı"><Input value={s.site_title} onChange={(e) => set("site_title", e.target.value)} data-testid="settings-site-title"/></Field>
        <Field label="Site Açıklaması"><Input value={s.site_description} onChange={(e) => set("site_description", e.target.value)} /></Field>
      </Section>

      <Section title="Hero Bölümü">
        <Field label="Hero Başlık"><Input value={s.hero_title} onChange={(e) => set("hero_title", e.target.value)} /></Field>
        <Field label="Hero Alt Başlık"><Input value={s.hero_subtitle} onChange={(e) => set("hero_subtitle", e.target.value)} /></Field>
        <Field label="Hero Açıklama"><Textarea rows={3} value={s.hero_description} onChange={(e) => set("hero_description", e.target.value)} /></Field>
      </Section>

      <Section title="Hakkımızda">
        <Field label="Başlık"><Input value={s.about_title} onChange={(e) => set("about_title", e.target.value)} /></Field>
        <Field label="Metin"><Textarea rows={5} value={s.about_text} onChange={(e) => set("about_text", e.target.value)} /></Field>
      </Section>

      <Section title="Koleksiyon Bölümü">
        <Field label="Başlık"><Input value={s.collection_section_title} onChange={(e) => set("collection_section_title", e.target.value)} /></Field>
        <Field label="Alt Başlık"><Input value={s.collection_section_subtitle} onChange={(e) => set("collection_section_subtitle", e.target.value)} /></Field>
      </Section>

      <Section title="İletişim">
        <Field label="Başlık"><Input value={s.contact_title} onChange={(e) => set("contact_title", e.target.value)} /></Field>
        <Field label="Telefon"><Input value={s.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} /></Field>
        <Field label="E-posta"><Input value={s.contact_email} onChange={(e) => set("contact_email", e.target.value)} /></Field>
        <Field label="Adres"><Textarea rows={2} value={s.contact_address} onChange={(e) => set("contact_address", e.target.value)} /></Field>
      </Section>

      <Section title="Sosyal Medya">
        <Field label="Instagram URL"><Input value={s.social_instagram} onChange={(e) => set("social_instagram", e.target.value)} /></Field>
        <Field label="Facebook URL"><Input value={s.social_facebook} onChange={(e) => set("social_facebook", e.target.value)} /></Field>
        <Field label="WhatsApp"><Input value={s.social_whatsapp} onChange={(e) => set("social_whatsapp", e.target.value)} /></Field>
      </Section>

      <Button onClick={save} disabled={saving} className="btn-luxe" data-testid="settings-save-btn">
        {saving ? "Kaydediliyor…" : "Tüm Ayarları Kaydet"}
      </Button>

      <PasswordSection />
    </div>
  );
}

function PasswordSection() {
  const [current, setCurrent] = useState("");
  const [next1, setNext1] = useState("");
  const [next2, setNext2] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!current || !next1) {
      toast.error("Tüm alanları doldurun");
      return;
    }
    if (next1.length < 6) {
      toast.error("Yeni şifre en az 6 karakter olmalı");
      return;
    }
    if (next1 !== next2) {
      toast.error("Yeni şifreler eşleşmiyor");
      return;
    }
    setSaving(true);
    try {
      await api.put("/auth/change-password", { current_password: current, new_password: next1 });
      toast.success("Şifre değiştirildi");
      setCurrent(""); setNext1(""); setNext2("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hata");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Section title="Şifre Değiştir">
      <Field label="Mevcut Şifre">
        <PasswordInput value={current} onChange={(e) => setCurrent(e.target.value)} testid="settings-current-password" autoComplete="current-password" />
      </Field>
      <Field label="Yeni Şifre">
        <PasswordInput value={next1} onChange={(e) => setNext1(e.target.value)} testid="settings-new-password" autoComplete="new-password" />
      </Field>
      <Field label="Yeni Şifre (Tekrar)">
        <PasswordInput value={next2} onChange={(e) => setNext2(e.target.value)} testid="settings-new-password-confirm" autoComplete="new-password" />
      </Field>
      <Button onClick={submit} disabled={saving} className="btn-luxe" data-testid="settings-change-password-btn">
        {saving ? "Değiştiriliyor…" : "Şifreyi Değiştir"}
      </Button>
    </Section>
  );
}

const Section = ({ title, children }) => (
  <div className="bg-white border border-[#eae5dc] p-6 space-y-4">
    <h3 className="font-serif text-xl">{title}</h3>
    {children}
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs uppercase tracking-wider text-[#6b655c] block mb-1.5">{label}</label>
    {children}
  </div>
);
